import React, { useCallback, useMemo, useState } from 'react';

import { Box, Icon, View } from '@rbilabs/universal-components';
import { ClearStoreSmButton } from 'features/store/pages/store-locator/components/clear-store-sm-button';
import { useIntl } from 'react-intl';
import { TouchableOpacity } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { CardModal } from 'components/card-modal';
import { useIsLowHeightDevice } from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { theme } from 'styles/configure-theme';

import { ListView, SearchBar, StoreChangeDisclaimer } from './components';
import { CancelSearchIcon } from './components/header/cancel-search-icon';
import { MapView as MapViewExperiment } from './components/map-view/map.view';
import { IStoreLocatorViewProps } from './components/types';
import { SearchBarWrapper, SearchContainer, TopSelectionContainer } from './store-locator.styled';

/**
 * This is the main component for the store locator.
 * It is responsible for handling the state of the store locator and the logic for the map and list view.
 */
export const StoreLocatorView: React.FC<IStoreLocatorViewProps> = ({
  searchingNearby,
  searchingRecent,
  searchingFavs,
  storesByTab,
  storesById,
  isInSupportForm,
  actions,
  currentTabKey,
  currentTabIndex,
  currentTabHasError,
  focusedRestaurant,
  locatorTabs,
  activeStore,
  allStores,
  flatListRef,
  mapRef,
  closestStore,
  onTabChange,
  onSelectStore,
  onDismissCallback,
  onFavoriteClick,
  onSearchThisArea,
  onResetUserCoords,
  onRestaurantHighlight,
  onResetManualSearch,
  onAddressSelected,
}) => {
  const { formatMessage } = useIntl();
  const isLowHeightDevice = useIsLowHeightDevice();

  const { isPermissionGranted } = useGeolocation();

  const [searchFieldActive, setSearchFieldActive] = useState<boolean>(!isPermissionGranted);
  const showSearchBar = searchFieldActive || isInSupportForm;

  const onToggleSearchField = useCallback(() => {
    setSearchFieldActive(prevSearchFieldActive => {
      // When the user press the search field, we need to change the tab to the nearby tab
      if (currentTabKey !== OrderLocatorTab.NEARBY && prevSearchFieldActive === false) {
        onTabChange(OrderLocatorTab.NEARBY);
      }
      return !prevSearchFieldActive;
    });
  }, [currentTabKey, onTabChange]);

  const onPressSearchThisArea = useCallback(
    (location: ILocation) => {
      onTabChange(OrderLocatorTab.NEARBY);
      onSearchThisArea(location);
    },
    [onSearchThisArea, onTabChange]
  );

  const tabSearchStatusMap = useMemo<{ [K in OrderLocatorTab]: boolean }>(
    () => ({
      [OrderLocatorTab.NEARBY]: searchingNearby,
      [OrderLocatorTab.RECENT]: searchingRecent,
      [OrderLocatorTab.FAVORITE]: searchingFavs,
      [OrderLocatorTab.DELIVERY]: false,
    }),
    [searchingNearby, searchingRecent, searchingFavs]
  );

  const handleOnCenterToUserLocation = useCallback(async () => {
    if (!isPermissionGranted) {
      return;
    }
    onResetUserCoords();
  }, [isPermissionGranted, onResetUserCoords]);

  const handleOnAddressSelected = useCallback(
    (address: string, placeId?: string) => {
      if (address) {
        onAddressSelected(address, placeId);
      } else {
        onResetManualSearch();
      }
    },
    [onAddressSelected, onResetManualSearch]
  );

  return (
    <CardModal
      testID="store-locator-wrapper"
      headerText={formatMessage({ id: 'selectLocation' })}
      alwaysHideHeader={isInSupportForm}
      invertHeaderColor
      onDismiss={onDismissCallback}
      customHeaderLeft={
        <TouchableOpacity testID="search-store-sm-button" onPress={onToggleSearchField}>
          {searchFieldActive ? (
            <CancelSearchIcon aria-hidden />
          ) : (
            <Icon variant="search" color={theme.token('text-button-reversed')} aria-hidden />
          )}
        </TouchableOpacity>
      }
    >
      <TopSelectionContainer>
        <SearchContainer marginBottom={showSearchBar ? '$4' : undefined}>
          {showSearchBar && (
            <SearchBarWrapper>
              <SearchBar onAddressSelected={handleOnAddressSelected} />
            </SearchBarWrapper>
          )}
        </SearchContainer>
      </TopSelectionContainer>

      <View>
        <StoreChangeDisclaimer marginX="$4" marginTop="$2" marginBottom="$4" />
      </View>

      <View style={{ flex: 1 }}>
        <ClearStoreSmButton />
        {/* If is lowHeightDevice, the map will take up the middle of the screen.
         * Otherwise, the map will take up 2/3 of the screen and the list will take up 1/3 of the screen.
         * This is a workaround to fix the issue with the empty states into the list view is not being visible on some devices.
         */}
        <Box flex={isLowHeightDevice ? 1 : 1.5}>
          <MapViewExperiment
            onMarkerPress={onRestaurantHighlight}
            onSelectStore={onSelectStore}
            onCenterToUserLocation={handleOnCenterToUserLocation}
            onPressSearchThisArea={onPressSearchThisArea}
            stores={allStores}
            storesFavs={storesByTab[OrderLocatorTab.FAVORITE]}
            restaurantFocused={focusedRestaurant}
            isInSupportForm={isInSupportForm}
            actions={actions}
            closestStore={closestStore}
            activeStore={activeStore}
            ref={mapRef}
            searchingNearby={searchingNearby}
          />
        </Box>
        <ListView
          onRestaurantHighlight={onRestaurantHighlight}
          onSelectStore={onSelectStore}
          onTabChange={onTabChange}
          onFavoriteClick={onFavoriteClick}
          locatorTabs={locatorTabs}
          storesByTab={storesByTab}
          isInSupportForm={isInSupportForm}
          restaurantFocused={focusedRestaurant ?? null}
          currentTabKey={currentTabKey}
          currentTabIndex={currentTabIndex}
          currentTabHasError={currentTabHasError}
          flatListRef={flatListRef}
          storesById={storesById}
          actions={actions}
          tabSearchStatusMap={tabSearchStatusMap}
        />
      </View>
    </CardModal>
  );
};
